import { deserializeProviderCookie, mergeProviderCookie, serializeProviderCookie } from "./core";

import type { ProviderCookieStore, ProviderCookieValue } from "./types";

/**
 * The raw storage a runtime provides. Everything above this — naming, encoding,
 * merge order, what `update` returns — is decided once in
 * {@link createProviderCookieStore}, so an adapter is only ever three lines of I/O.
 */
export interface ProviderCookieStoragePort {
  /**
   * Whatever the runtime holds for the cookie: a JSON string (server jars) or an
   * already-parsed object (the client cookie library parses JSON cookies).
   */
  readRaw: () => unknown;
  writeValue: (value: ProviderCookieValue) => void;
  clear: () => void;
}

/**
 * Builds a store from a storage port. `update` re-reads immediately before
 * merging so a caller's partial always lands on the freshest value rather than on
 * a snapshot it took earlier, and returns the value as *stored* (round-tripped
 * through serialization) so the return value can never disagree with a
 * subsequent `read()`.
 */
export const createProviderCookieStore = (
  storage: ProviderCookieStoragePort
): ProviderCookieStore => {
  const read = () => deserializeProviderCookie(storage.readRaw());

  const replace = (value: ProviderCookieValue) => {
    storage.writeValue(value);
  };

  return {
    read,
    update: (partial) => {
      const merged = mergeProviderCookie(read(), partial);
      replace(merged);
      // Serialization drops keys set to `undefined`; round-trip so callers see
      // exactly what the cookie now holds. Never undefined: serializing an
      // object always yields a parseable object.
      return deserializeProviderCookie(serializeProviderCookie(merged)) as ProviderCookieValue;
    },
    replace,
    clear: storage.clear,
  };
};
